import { useEffect, useState } from "react";
import { Focus, User, Building2, FolderKanban, Globe2, Check } from "lucide-react";
import Modal from "@/components/Modal";
import { useLocalContext, describeScope } from "@/lib/moduleContext";
import { api } from "@/lib/api";

/**
 * Header-level "Contexte" button that opens a modal letting the user define
 * the scope NOAM should use when generating analyses for THIS module.
 *
 * Hybrid input on "project" and "external" options: a native <datalist>
 * surfaces existing Noam objects (Pipeline deals, Courtage mandates,
 * Immobilier assets, Suppliers, Utility actors) as suggestions while still
 * accepting free typing.
 *
 * Usage:
 *   <ContextButton moduleKey="risques" />
 */
const OPTIONS = [
  { type: "self", label: "Mon profil personnel", icon: User, hint: "Recommandations centrées sur vos décisions personnelles." },
  { type: "org", label: "Mon organisation", icon: Building2, hint: "Périmètre par défaut — toute votre organisation." },
  { type: "project", label: "Un projet spécifique", icon: FolderKanban, hint: "Restreindre l'IA à un projet ou une opportunité précis." },
  { type: "external", label: "Une autre organisation", icon: Globe2, hint: "Analyser une contrepartie externe (saisie libre ou existante)." },
];

export default function ContextButton({ moduleKey, className = "", fullWidth = false }) {
  const [scope, setScope] = useLocalContext(moduleKey);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          fullWidth
            ? `w-full inline-flex items-center gap-2 text-xs text-zinc-200 hover:text-white transition-colors text-left ${className}`
            : `inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/10 text-xs text-zinc-300 hover:text-white hover:border-white/20 transition-colors ${className}`
        }
        title="Définir le contexte de l'analyse IA"
        data-testid={`context-btn-${moduleKey}`}
      >
        <Focus className="w-3.5 h-3.5 text-[#EF9F27] shrink-0" />
        <span className={fullWidth ? "" : "hidden sm:inline"}>Contexte&nbsp;:&nbsp;</span>
        <span className="font-medium truncate" data-testid={`context-badge-${moduleKey}`}>{describeScope(scope)}</span>
      </button>
      {open && (
        <ContextModal
          moduleKey={moduleKey}
          current={scope}
          onClose={() => setOpen(false)}
          onSave={(next) => { setScope(next); setOpen(false); }}
        />
      )}
    </>
  );
}

function ContextModal({ moduleKey, current, onClose, onSave }) {
  const [type, setType] = useState(current?.type || "org");
  const [projectLabel, setProjectLabel] = useState(current?.type === "project" ? (current.label || "") : "");
  const [externalName, setExternalName] = useState(current?.type === "external" ? (current.name || "") : "");
  const [sources, setSources] = useState({ projects: [], organisations: [] });

  useEffect(() => {
    api.get("/workspace/context-sources").then((r) => {
      setSources({
        projects: r.data.projects || [],
        organisations: r.data.organisations || [],
      });
    }).catch(() => {});
  }, []);

  const submit = (e) => {
    e?.preventDefault();
    if (type === "project") onSave({ type, label: projectLabel.trim() || "Projet sans nom" });
    else if (type === "external") onSave({ type, name: externalName.trim() || "Organisation externe" });
    else onSave({ type });
  };

  const projectsListId = `ctx-projects-${moduleKey}`;
  const orgsListId = `ctx-orgs-${moduleKey}`;

  return (
    <Modal onClose={onClose} maxWidth="max-w-lg" testid={`context-modal-${moduleKey}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline flex items-center gap-1.5 text-[#EF9F27]"><Focus className="w-3 h-3" /> Contexte de l'analyse</div>
          <div className="noam-display text-lg font-semibold mt-1">À quel périmètre NOAM doit-il adapter ses recommandations ?</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Ce choix est mémorisé pour ce module uniquement.</p>
        </div>
      </div>

      <datalist id={projectsListId}>
        {sources.projects.map((p) => (
          <option key={`${p.kind}-${p.id}`} value={p.label}>{p.sub ? `${p.kind} · ${p.sub}` : p.kind}</option>
        ))}
      </datalist>
      <datalist id={orgsListId}>
        {sources.organisations.map((o) => (
          <option key={`${o.kind}-${o.id}`} value={o.label}>{o.sub ? `${o.kind} · ${o.sub}` : o.kind}</option>
        ))}
      </datalist>

      <form onSubmit={submit} className="space-y-2">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = type === opt.type;
          return (
            <button
              type="button"
              key={opt.type}
              onClick={() => setType(opt.type)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${active ? "border-[#EF9F27] bg-[#EF9F27]/5" : "border-white/10 hover:border-white/20"}`}
              data-testid={`context-option-${opt.type}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-[#EF9F27]/15 text-[#EF9F27]" : "bg-white/5 text-zinc-400"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white">{opt.label}</div>
                  <div className="text-[11px] text-zinc-500">{opt.hint}</div>
                </div>
                {active && <Check className="w-4 h-4 text-[#EF9F27] shrink-0" />}
              </div>
              {active && opt.type === "project" && (
                <div className="mt-3 pl-11 space-y-1.5">
                  <input
                    autoFocus
                    list={projectsListId}
                    value={projectLabel}
                    onChange={(e) => setProjectLabel(e.target.value)}
                    placeholder="Choisir ou saisir un projet…"
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#EF9F27] focus:outline-none"
                    data-testid="context-input-project"
                  />
                  <div className="text-[10px] text-zinc-500">
                    {sources.projects.length} projet(s) Noam suggéré(s) — vous pouvez aussi saisir librement.
                  </div>
                </div>
              )}
              {active && opt.type === "external" && (
                <div className="mt-3 pl-11 space-y-1.5">
                  <input
                    autoFocus
                    list={orgsListId}
                    value={externalName}
                    onChange={(e) => setExternalName(e.target.value)}
                    placeholder="Choisir ou saisir une organisation…"
                    className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#EF9F27] focus:outline-none"
                    data-testid="context-input-external"
                  />
                  <div className="text-[10px] text-zinc-500">
                    {sources.organisations.length} organisation(s) Noam suggérée(s) — saisie libre possible.
                  </div>
                </div>
              )}
            </button>
          );
        })}

        <div className="flex items-center justify-end gap-2 pt-3">
          <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
          <button type="submit" className="btn-primary text-xs" data-testid="context-save">
            <Check className="w-3.5 h-3.5" /> Appliquer
          </button>
        </div>
      </form>
    </Modal>
  );
}
